import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";

import { BorderBox } from "@/components/atoms/BorderBox";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useFieldContext } from "@/features/question/components/organisms/FieldRenderer";
import { z } from "zod";

export function RankRenderer() {
    const { question, disabled, response, form, currentValue } = useFieldContext();

    const [options, setOptions] = useState<string[]>(z.array(z.string()).parse(question?.field_options) || []);

    useEffect(() => {
        if(currentValue) {
            setOptions(currentValue);
        }
    }, [currentValue]);

    useEffect(() => {
        if (!form) return
        form.setValue('additional', options.join(','));
    }, [options, form]);

    const onDragEnd: OnDragEndResponder = (result) => {
        const { destination, source } = result;

        // Do nothing if dropped outside the list
        if (!destination) {
            return;
        }

        // Do nothing if dropped in the same position
        if (destination.index === source.index) {
            return;
        }

        // Use the current options state, not fieldData
        const currentOptions = [...options];

        // Remove the item from source position
        const [reorderedItem] = currentOptions.splice(source.index, 1);

        // Insert the item at destination position
        currentOptions.splice(destination.index, 0, reorderedItem);

        // Update both local state and form value
        setOptions(currentOptions);
    };
    return (
        <div className="w-full">
            {!disabled ? (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="options">
                        {(droppableProvided, snapshot) => (
                            <div
                                className={cn('flex flex-col gap-1 rounded-md p-1 transition-all', {
                                    "bg-lighter-gray": snapshot.isDraggingOver
                                })}
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                            >
                                {options?.map((option, i) => {
                                    return (
                                        <Draggable
                                            key={option}
                                            draggableId={option}
                                            index={i}
                                        >
                                            {(provided, snapshot) => {
                                                const usePortal = snapshot.isDragging;

                                                const child = (
                                                    <BorderBox
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={cn('flex items-start px-2 py-1', {
                                                            'opacity-80': snapshot.isDragging
                                                        })}
                                                        style={provided.draggableProps.style}
                                                    >
                                                        <div className="">
                                                            {(question.field_options?.indexOf(option.trim()) ?? 0) + 1}.&nbsp;
                                                        </div>
                                                        <div className="">
                                                            {option}
                                                        </div>
                                                    </BorderBox>
                                                );

                                                if (!usePortal) {
                                                    return child;
                                                }

                                                // if dragging - put the item in a portal
                                                return createPortal(child, document.body);
                                            }}
                                        </Draggable>
                                    );
                                })}
                                {droppableProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            ) : (
                <div className="flex flex-col gap-1">
                    {response?.field_value?.split(',')?.map((option, i) => (
                        <BorderBox
                            key={`${option}-${i}`}
                            className="px-2 py-1 bg-lighter-gray"
                        >
                            {(question.field_options?.indexOf(option.trim()) ?? 0) + 1}.&nbsp; {option}
                        </BorderBox>
                    ))}
                </div>
            )}
        </div>
    );
}